
import { useState } from "react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Mail, MessageSquare, Send } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { Helmet } from "react-helmet";

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Prepare email body
    const emailBody = `
Name: ${formData.name}
Email: ${formData.email}
Subject: ${formData.subject}

${formData.message}
    `;
    
    // Create mailto link
    const mailtoLink = `mailto:info@unitcodex.com?subject=${encodeURIComponent(formData.subject)}&body=${encodeURIComponent(emailBody)}`;
    
    // Open email client
    window.location.href = mailtoLink;
    
    // Show success message and reset form after a brief delay
    setTimeout(() => {
      setIsSubmitting(false);
      setFormData({
        name: "",
        email: "",
        subject: "",
        message: ""
      });
      
      toast({
        title: "Email Client Opened",
        description: "Your message has been prepared. Please send the email from your email client.",
        duration: 5000,
      });
    }, 500);
  };

  return (
    <div className="py-8">
      <Helmet>
        <title>Contact UnitCodex - Get in Touch</title>
        <meta name="description" content="Have questions about historical units? Contact the UnitCodex team for assistance with conversions or to learn more about our measurement history resources." />
        <link rel="canonical" href="https://unitcodex.com/contact" />
      </Helmet>
      
      <div className="container">
        <header className="text-center mb-8">
          <h1 className="text-4xl font-cinzel text-manuscript-primary mb-3">
            Contact Us
          </h1>
          <p className="text-manuscript-primary/70 max-w-2xl mx-auto">
            Have questions about historical units or need assistance with conversions? 
            We're here to help. Reach out to us using any of the methods below.
          </p>
        </header>

        <div className="max-w-6xl mx-auto px-4 grid gap-8 md:grid-cols-2 mb-12">
          <Card className="bg-white/80 backdrop-blur-sm">
            <CardHeader className="text-center">
              <Mail className="w-10 h-10 text-manuscript-primary mx-auto mb-2" />
              <CardTitle className="text-xl font-cinzel">Email Us</CardTitle>
            </CardHeader>
            <CardContent className="text-center">
              <p className="text-manuscript-primary/80">
                <a href="mailto:info@unitcodex.com" className="underline hover:text-manuscript-primary transition-colors">
                  info@unitcodex.com
                </a>
              </p>
              <p className="mt-2 text-sm text-manuscript-primary/60">
                We aim to respond to all inquiries within 24-48 hours.
              </p>
            </CardContent>
          </Card>

          <Card className="bg-white/80 backdrop-blur-sm">
            <CardHeader className="text-center">
              <MessageSquare className="w-10 h-10 text-manuscript-primary mx-auto mb-2" />
              <CardTitle className="text-xl font-cinzel">Support Hours</CardTitle>
            </CardHeader>
            <CardContent className="text-center">
              <p className="text-manuscript-primary/80">
                Monday - Friday<br />
                9:00 AM - 5:00 PM PST
              </p>
              <p className="mt-2 text-sm text-manuscript-primary/60">
                Weekend inquiries will be addressed on the following business day.
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="max-w-2xl mx-auto">
          <Card className="bg-white/80 backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="text-2xl font-cinzel text-manuscript-primary">Send Us a Message</CardTitle>
              <CardDescription>
                Fill out the form below and we'll get back to you as soon as possible.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="name">Your Name</Label>
                    <Input 
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="John Doe"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address</Label>
                    <Input 
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="johndoe@example.com"
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="subject">Subject</Label>
                  <Input 
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder="Question about historical units"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="message">Your Message</Label>
                  <Textarea 
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Type your message here..."
                    rows={5}
                    required
                  />
                </div>
              </form>
            </CardContent>
            <CardFooter>
              <Button 
                onClick={handleSubmit}
                className="w-full flex items-center gap-2"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Opening Email Client..." : "Send Message"}
                <Send className="w-4 h-4" />
              </Button>
            </CardFooter>
          </Card>
        </div>

        <div className="max-w-3xl mx-auto mt-12 text-center">
          <h2 className="text-2xl font-cinzel text-manuscript-primary mb-4">Frequently Asked Questions</h2>
          <p className="text-manuscript-primary/70 mb-2">
            For common questions about our historical unit conversion tools, please visit our
            <a href="/faqs" className="text-manuscript-primary mx-1 underline hover:text-manuscript-primary/80 transition-colors">
              FAQs page
            </a>
            where you might find the answer you're looking for.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Contact;
